import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Terminal, Trash2, ExternalLink, AlertTriangle, Loader2, LogOut } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    // Check auth
    const isAuth = sessionStorage.getItem('admin_auth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }

    fetchAnalyses();
  }, [navigate]);

  const fetchAnalyses = async () => {
    try {
      const response = await axios.get(`${API}/admin/analyses`);
      setAnalyses(response.data.analyses);
    } catch (error) {
      console.error('Error fetching analyses:', error);
      toast.error('Failed to load analyses');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/analyses/${id}`);
      toast.success('Analysis deleted successfully');
      setAnalyses(analyses.filter(a => a.id !== id));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting analysis:', error);
      toast.error('Failed to delete analysis');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    toast.success('Logged out');
    navigate('/admin');
  };

  const getRiskColor = (label) => {
    switch (label) {
      case "Low":
        return "bg-green-500/20 text-green-400 border-green-500/50";
      case "Medium":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      case "High":
        return "bg-red-500/20 text-red-400 border-red-500/50";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-gray-100">
      {/* Header */}
      <div className="border-b border-cyan-500/20 bg-black/40 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Terminal className="w-8 h-8 text-cyan-400" />
              <div>
                <h1 className="text-2xl font-bold font-mono text-cyan-400 tracking-wider">ADMIN PANEL</h1>
                <p className="text-xs text-gray-500 font-mono">// Manage all analyses</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-gray-600 text-gray-400 hover:bg-gray-800/50 font-mono"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                VIEW SITE
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono"
              >
                <LogOut className="w-4 h-4 mr-2" />
                LOGOUT
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h2 className="text-xl font-mono text-gray-100 mb-2">ALL ANALYSES</h2>
          <p className="text-sm text-gray-500 font-mono">Total: {analyses.length} analyses</p>
        </div>

        {analyses.length === 0 ? (
          <Card className="border-cyan-500/30 bg-black/60">
            <CardContent className="py-12 text-center text-gray-400 font-mono">
              No analyses found
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {analyses.map((analysis) => (
              <Card key={analysis.id} className="border-cyan-500/30 bg-black/60 backdrop-blur-sm">
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-mono text-gray-100 mb-2">{analysis.title}</CardTitle>
                      <CardDescription className="text-xs text-gray-500 font-mono flex items-center gap-2">
                        <ExternalLink className="w-3 h-3" />
                        {analysis.source_domain}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={`${getRiskColor(analysis.risk_label)} font-mono`}>
                        {analysis.risk_label}
                      </Badge>
                      <div className="text-right">
                        <div className="text-2xl font-bold font-mono text-cyan-400">{analysis.total_score}</div>
                        <div className="text-xs text-gray-500 font-mono">/100</div>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-xs text-gray-500 font-mono">
                        ID: {analysis.id}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">
                        {new Date(analysis.timestamp).toLocaleString()}
                      </p>
                      {analysis.url && (
                        <a
                          href={analysis.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-cyan-400 hover:text-cyan-300 font-mono flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Original Article
                        </a>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => navigate(`/analysis/${analysis.id}`)}
                        variant="outline"
                        size="sm"
                        className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 font-mono"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        VIEW
                      </Button>
                      {deleteConfirm === analysis.id ? (
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => handleDelete(analysis.id)}
                            disabled={deleting}
                            size="sm"
                            className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 font-mono"
                          >
                            {deleting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                CONFIRM
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => setDeleteConfirm(null)}
                            disabled={deleting}
                            variant="outline"
                            size="sm"
                            className="border-gray-600 text-gray-400 hover:bg-gray-800/50 font-mono"
                          >
                            CANCEL
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setDeleteConfirm(analysis.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          DELETE
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;

